import React, {useEffect, useState} from 'react';
import {Button, Callout, Checkbox, Drawer, FormGroup, HTMLSelect, Tag} from '@blueprintjs/core';
import styled from 'styled-components';

import {SearchableDataGrid} from "../../../../components/tables/data-grid";
import * as AuthAPI from '../../../../services/auth-api';
import {timeAgo} from "../../../../utils/time";


const DataWrapper = styled.div`
    width: 95%;
    margin: 2em auto;
`;

const CalloutWrapper = styled.div`
    width: 95%;
    margin: 0 auto 2em auto;
`;

const capabilityColumns = [
    {key: 'id', name: 'Id', width: 50, sortable: true, filterable: true},
    {
        key: 'resource', name: 'Resource', width: 175, resizable: true, sortable: true, filterable: true,
        formatter: (params) => <Tag intent="primary">{params.row.resource}</Tag>
    },
    {
        key: 'canCreate', name: 'Create', width: 65, resizable: true,
        formatter: (params) => <Checkbox checked={!!params.row.canCreate} readOnly />
    },
    {
        key: 'canRead', name: 'Read', width: 65, resizable: true,
        formatter: (params) => <Checkbox checked={!!params.row.canRead} readOnly />
    },
    {
        key: 'canUpdate', name: 'Update', width: 65, resizable: true,
        formatter: (params) => <Checkbox checked={!!params.row.canUpdate} readOnly />
    },
    {
        key: 'canDelete', name: 'Delete', width: 65, resizable: true,
        formatter: (params) => <Checkbox checked={!!params.row.canDelete} readOnly />
    },
    {key: 'visibility', name: 'Visibility', width: 120, resizable: true, sortable: true, filterable: true},
    {key: 'name', name: 'Name', resizable: true, sortable: true, filterable: true},
    {
        key: 'auraAdminOnly', name: 'Internal only', width: 130, resizable: true,
        formatter: (params) => <Checkbox checked={!!params.row.auraAdminOnly} readOnly />
    },
    {
        key: 'createdAt', name: 'Created', width: 100, resizable: true, sortable: true,
        formatter: (params) =>
            <span title={params.row.createdAt}>{timeAgo(params.row.createdAt)}</span>,
    },
    {
        key: 'updatedAt', name: 'Updated', width: 100, resizable: true, sortable: true,
        formatter: (params) =>
            <span title={params.row.updatedAt}>{timeAgo(params.row.updatedAt)}</span>,
    },
];


const CapabilitiesForRole = ({isOpen, onClose, role}) => {
    const [data, setData] = useState([]);
    const [allCapabilities, setAllCapabilities] = useState([]);
    const [selectedCapId, setSelectedCapId] = useState(null);
    const [response, setResponse] = useState({});

    const fetchCapabilities = () => {
        if (!role) return;
        Promise.all([
            AuthAPI.adminRoles_capabilities.list(role.id),
            AuthAPI.capabilities.list(),
        ]).then(([capsRes, allCapsRes]) => {
            setData(capsRes.data);
            setAllCapabilities(allCapsRes.data);
        }).catch(e => {
            console.error(e);
        });
    };

    useEffect(() => {
        if (isOpen) {
            fetchCapabilities();
            setResponse({});
        } else {
            setData([]);
            setAllCapabilities([]);
            setSelectedCapId(null);
        }
    }, [isOpen, role]);

    const addCapability = () => {
        if (!selectedCapId) return;
        setResponse({type: 'primary', text: 'Loading...'});
        AuthAPI.adminRoles_capabilities.create(role.id, selectedCapId).then(() => {
            setResponse({type: 'success', text: 'Capability added to role'});
            fetchCapabilities();
        }).catch(() => {
            setResponse({type: 'danger', text: 'Could not add capability to role'});
        });
    };

    return (
        <Drawer
            size={Drawer.SIZE_LARGE}
            icon="shield"
            title={role ? `Role > ${role.name} > Capabilities` : ''}
            isOpen={isOpen}
            onClose={onClose}
        >
            <DataWrapper>
                <SearchableDataGrid
                    columns={capabilityColumns}
                    data={data}
                    defaultSort='id'
                />
            </DataWrapper>
            <CalloutWrapper>
                <Callout title="Assign a new capability to this role">
                    <FormGroup>
                        <HTMLSelect
                            onChange={(e) => setSelectedCapId(e.target.value)}
                            value={selectedCapId || ''}
                            options={
                                allCapabilities.length === 0
                                    ? [{label: 'Loading...', value: ''}]
                                    : [{label: 'Select a capability...', value: ''}, ...allCapabilities.map(cap => ({
                                        label: `${cap.name} (id: ${cap.id})`,
                                        value: cap.id
                                    }))]
                            }
                        />
                        <Button text="Add" intent="primary" onClick={addCapability} style={{marginTop: 8}} />
                    </FormGroup>
                </Callout>
                {response.type && (
                    <Callout intent={response.type} style={{marginTop: 8}}>
                        {response.text}
                    </Callout>
                )}
            </CalloutWrapper>
        </Drawer>
    );
};

export {CapabilitiesForRole};
